import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { useParams } from "react-router-dom";
import { fetchSliders, preloadHeroImage, dismissLcpHeroShell, isLcpHeroShellVisible } from "../../config/firestoreRest";

const HeroCarousel = lazy(() => import("./HeroCarousel"));

const DESKTOP_MQ = "(min-width: 768px)";
const SLIDERS_CACHE_KEY = "sliders-jeep";

function readCachedBanners() {
  try {
    const cached = sessionStorage.getItem(SLIDERS_CACHE_KEY);
    return cached ? JSON.parse(cached) : [];
  } catch {
    return [];
  }
}

function getBannerItems(banners, tipo, isDesktop) {
  if (!banners[0]) return [];
  const items = banners[0][isDesktop ? "desktop" : "mobile"] || [];

  if (!tipo || tipo === "todos" || tipo === "preventa") return items;
  if (tipo === "plan") {
    const filtered = items.filter((b) => b.plan === true);
    return filtered.length > 0 ? filtered : items;
  }
  if (tipo === "convencional") {
    const filtered = items.filter((b) => b.convencional === true);
    return filtered.length > 0 ? filtered : items;
  }
  return items;
}

const HeroImage = ({ src, priority = "high", lazy = false }) => (
  <img
    className="d-block w-100 img-fluid hero-slider__img"
    src={src}
    alt="Montironi Jeep"
    loading={lazy ? "lazy" : "eager"}
    fetchPriority={priority}
    decoding="async"
    sizes="100vw"
  />
);

const Slider = () => {
  const [banners, setBanners] = useState(readCachedBanners);
  const [loading, setLoading] = useState(() => readCachedBanners().length === 0);
  const [carouselReady, setCarouselReady] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(DESKTOP_MQ).matches : true,
  );
  const { tipo } = useParams();

  const showTodosSlider = !tipo || tipo === "todos" || tipo === "preventa";
  const showSlider = showTodosSlider || tipo === "plan" || tipo === "convencional";
  const sliderType = showTodosSlider ? "todos" : tipo;

  const handleCarouselReady = useCallback(() => {
    setCarouselReady(true);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_MQ);
    const sync = () => setIsDesktop(mq.matches);
    sync();
    if (mq.addEventListener) {
      mq.addEventListener("change", sync);
      return () => mq.removeEventListener("change", sync);
    }
    mq.addListener(sync);
    return () => mq.removeListener(sync);
  }, []);

  useEffect(() => {
    if (!loading) return undefined;

    let cancelled = false;

    const obtenerDatos = async () => {
      try {
        const cached = sessionStorage.getItem(SLIDERS_CACHE_KEY);
        if (cached) {
          if (!cancelled) {
            setBanners(JSON.parse(cached));
            setLoading(false);
          }
          return;
        }

        const arrayData = await fetchSliders();
        if (cancelled) return;
        sessionStorage.setItem(SLIDERS_CACHE_KEY, JSON.stringify(arrayData));
        setBanners(arrayData);
      } catch (error) {
        console.error("Error al obtener datos de Firestore: ", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    obtenerDatos();
    return () => {
      cancelled = true;
    };
  }, [loading]);

  const screen = isDesktop ? "desktop" : "mobile";
  const items = getBannerItems(banners, sliderType, isDesktop);

  useEffect(() => {
    if (!showSlider) {
      dismissLcpHeroShell();
      return undefined;
    }
    if (!loading && items.length) {
      dismissLcpHeroShell();
    }
    return undefined;
  }, [showSlider, loading, items.length]);

  useEffect(() => {
    if (loading || !showSlider) return undefined;

    const firstUrl = items[0]?.slider;
    if (!firstUrl) return undefined;

    preloadHeroImage(firstUrl);
    return undefined;
  }, [banners, loading, showSlider, sliderType, isDesktop, items]);

  const handleSlideChange = (selectedIndex, bannerArray, screenName, type) => {
    if (window.dataLayer) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "view_promotion",
        ecommerce: {
          items: [
            {
              item_name: `URL:${bannerArray[selectedIndex]?.slider}` || "N/A",
              item_id: bannerArray[selectedIndex]?.id || "N/A",
              promotion_id: "N/A",
              promotion_name: `${type}`,
              creative_name: bannerArray[selectedIndex]?.creative_name || "N/A",
              creative_slot: `slider_${selectedIndex + 1}_${screenName}`,
              location_id: "top_body_home",
            },
          ],
        },
      });
    }
  };

  if (!showSlider) return null;

  if (loading) {
    if (isLcpHeroShellVisible()) return null;
    return <div id="inicio" className="hero-slider hero-slider--loading" aria-hidden="true" />;
  }

  if (!items.length) {
    return <div id="inicio" className="hero-slider hero-slider--loading" aria-hidden="true" />;
  }

  const firstSlideUrl = items[0].slider;
  const hasMultipleSlides = items.length > 1;

  return (
    <div id="inicio" className="hero-slider">
      {hasMultipleSlides && !carouselReady && (
        <HeroImage src={firstSlideUrl} priority="high" />
      )}

      {hasMultipleSlides ? (
        <Suspense fallback={null}>
          <HeroCarousel
            items={items}
            screen={screen}
            sliderType={sliderType}
            onSlideChange={handleSlideChange}
            onReady={handleCarouselReady}
          />
        </Suspense>
      ) : (
        <HeroImage src={firstSlideUrl} priority="high" />
      )}
    </div>
  );
};

export default Slider;
